import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, SortingRule, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { Trans, useTranslation } from 'react-i18next';
import { AppDispatch } from 'store/store';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { getUser } from 'store/selectors/auth.selector';
import { getIsModalsDisabled } from 'store/selectors/ui.selector';
import { AddVehicleDialog } from 'components/Modals/AddVehicleDialog/AddVehicleDialog';
import { thousandFormatter, userHasInventory } from 'modules/core/utils';

import { ItemsPerPage } from './ItemsPerPage';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { Pagination } from './Pagination';
import { AdjustDropdown } from './AdjustDropdown';
import { useSelectableColumns } from './plugins';
import { getHiddenColumnsList } from './helper';
import { ITableCallbacks, ITableSortBy, TableColumn, TableData } from './types';
import { INITIAL_PAGE_SIZE, SELECT_ALL_COLUMN_ID, TABLE_RENDER_HEADER_KEY } from './table.const';
import { Panel } from '../Panel/Panel';
import {
  NoVehicleErrorMessage,
  OverflowTableWrapper,
  SelectAllLabel,
  SelectAllSection,
  TableContainerWrapper,
  TableHeaderSection,
  TableItems,
  TableTopSection,
  TableWrapper,
  ToggleCardViewButton,
  ToggleTableViewButton,
  TotalItems,
} from './styles';
import { VehicleCardView } from './VehicleCardView';
import { IDealSourceData } from '../../types/dealSource';
import { Button } from '../Button/Button';
import { ButtonVariant, TableName, VehicleTablePlaceholderType } from '../../enums';
import { IRACVehicle } from '../../types';
import { useGenerateRandomVehicleList } from '../../hooks/useGenerateRandomVehicleList';
import { useAutoLoadInventory } from '../../hooks/useAutoLoadInventory';

interface ITable {
  title?: string;
  data: TableData[];
  columns: TableColumn[];
  defaultColumns?: TableColumn[];
  customerFriendlyColumns?: string[];
  pageCount: number;
  fetchRequestCallback?: <C>(pageIndex: number, pageSize: number, sortBy: SortingRule<C>[]) => Promise<void>;
  selectable?: boolean;
  isLoading?: boolean;
  isAdjustViewVisible?: boolean;
  withCardView?: boolean;
  renderAdditionalTableHeaderComponents?: (hiddenColumnsCallback: VoidFunction) => JSX.Element;
  tableCallbacks?: ITableCallbacks;
  children?: ReactNode;
  isTableFiltersDisabled?: boolean;
  isTableActionsDisabled?: boolean;
  isTableView?: boolean;
  setIsTableView?: ActionCreatorWithPayload<boolean, string>;
  tablePlaceholder?: JSX.Element;
  tablePlaceholderType: string;
  tableSortBy?: ITableSortBy[];
  CustomVehicleCard?: any;
  totalCount?: number;
  isServerPagination?: boolean;
  selectedRows?: TableData[];
  skipAutoStateReset?: boolean;
  handleOpenDialog?: () => void;
  tableSubmitCallback?: VoidFunction;
  tableName: TableName;
  showNoVehicleErrorMessage: boolean;
  showNoVehicleInInventoryErrorMessage: boolean;
  showNoAddedVehicleErrorMessage: boolean;
  showNoResultsAndDealSourceVehicle: boolean;
  dealSourceData: IDealSourceData;
  isMakeItWork: boolean;
  tier?: number | string;
  lenderName?: string;
  currentLenderId?: string;
  resetCount: number;
}

const Table: FC<ITable> = ({
  title,
  data,
  columns,
  customerFriendlyColumns,
  pageCount,
  children,
  fetchRequestCallback,
  renderAdditionalTableHeaderComponents,
  selectable = false,
  isAdjustViewVisible = true,
  withCardView = false,
  tableCallbacks,
  isTableFiltersDisabled,
  isTableActionsDisabled,
  isTableView,
  setIsTableView,
  tablePlaceholder,
  tablePlaceholderType,
  CustomVehicleCard,
  defaultColumns,
  totalCount,
  isServerPagination = false,
  selectedRows,
  skipAutoStateReset = false,
  handleOpenDialog,
  tableSubmitCallback,
  tableName,
  showNoVehicleErrorMessage,
  showNoVehicleInInventoryErrorMessage,
  showNoAddedVehicleErrorMessage,
  showNoResultsAndDealSourceVehicle,
  dealSourceData,
  isMakeItWork,
  tier,
  lenderName,
  currentLenderId,
  resetCount,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isAddVehicleModalDisabled = useSelector(getIsModalsDisabled);
  const user = useSelector(getUser);
  const { isSmallWidth } = useWindowDimensions();
  const [showAddVehicleDialog, setShowAddVehicleDialog] = useState<boolean>(false);
  const isRac = tableName === TableName.RAC;
  const isLender = tableName === TableName.LENDER;
  const isDealerWithInventory = userHasInventory(user);

  useAutoLoadInventory(tableSubmitCallback);

  const {
    setPageSize,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    setHiddenColumns,
    visibleColumns,
    toggleHideAllColumns,
    selectedFlatRows,
    gotoPage,
    state: { pageIndex, pageSize, sortBy, selectedRowIds },
  } = useTable(
    {
      data,
      columns,
      manualPagination: isServerPagination,
      getRowId: React.useCallback((row, relativeIndex) => (isServerPagination ? row.id : relativeIndex), [
        isServerPagination,
      ]),
      initialState: {
        pageIndex: 0,
        pageSize: INITIAL_PAGE_SIZE,
      },
      pageCount: pageCount,
      autoResetPage: !skipAutoStateReset,
      autoResetSelectedRows: !skipAutoStateReset,
      autoResetSortBy: !skipAutoStateReset,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useSelectableColumns(selectable),
  );

  const countAdditionalRows = INITIAL_PAGE_SIZE - data.length;

  const additionalVehicles = useGenerateRandomVehicleList(countAdditionalRows);

  const { rows: additionalRows } = useTable(
    {
      data: additionalVehicles,
      columns,
    },
    useRowSelect,
    useSelectableColumns(selectable),
  );

  const rowsIds = rows.map((row) => row.id).join();
  const selectedFlatRowsIds = selectedFlatRows.map((row) => row.id).join();

  useEffect(() => {
    if (isServerPagination || tablePlaceholderType === VehicleTablePlaceholderType.LOADING) return;

    const maxPageIndex = Math.max(Math.ceil(rows.length / pageSize) - 1, 0);

    if (pageIndex > maxPageIndex) {
      gotoPage(maxPageIndex);
    }
  }, [isServerPagination, tablePlaceholderType, rows.length, pageSize, pageIndex, gotoPage]);

  const { sortedRows, dealSourceVehicleRowIndex } = useMemo(() => {
    if (!rowsIds) return { sortedRows: [] as Row<TableData>[], dealSourceVehicleRowIndex: -1 };

    let nextDealSourceVehicleRowIndex = -1;

    const manuallyCreatedTrue = rows.filter((row) => row.original.manually_created);
    const manuallyCreatedFalse = rows.filter((row) => !row.original.manually_created);

    manuallyCreatedTrue.sort((a, b) => (b.original as IRACVehicle).id - (a.original as IRACVehicle).id);
    const sortedByManuallyCreated = manuallyCreatedTrue.concat(manuallyCreatedFalse);

    if (
      (isRac || isLender) &&
      isMakeItWork &&
      Object.keys(dealSourceData).length > 0 &&
      dealSourceData.vehicle_exists
    ) {
      const dealSourceVehicleId = (dealSourceData as IDealSourceData).vehicle_id;

      if (rows.length && rows.length === 1 && rows[0].original.id === dealSourceVehicleId)
        nextDealSourceVehicleRowIndex = rows[0].index;

      sortedByManuallyCreated.sort((a, b) => {
        if (a.original.id === dealSourceVehicleId) {
          nextDealSourceVehicleRowIndex = a.index;

          return -1;
        } else if (b.original.id === dealSourceVehicleId) {
          nextDealSourceVehicleRowIndex = b.index;

          return 1;
        } else return 0;
      });
    }

    return { sortedRows: sortedByManuallyCreated, dealSourceVehicleRowIndex: nextDealSourceVehicleRowIndex };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, rowsIds, isRac, isLender, isMakeItWork, dealSourceData]);

  const sortedRowsIds = sortedRows.map((row) => row.id).join();

  useEffect(() => {
    tableCallbacks?.sortedRowsCallback?.(sortedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedRowsIds]);

  const hiddenColumnsCallback = useCallback(() => {
    setHiddenColumns(getHiddenColumnsList(columns));
  }, [setHiddenColumns, columns]);

  const visibleAdjustmentViewColumns = useMemo(() => visibleColumns.filter(({ id }) => id !== SELECT_ALL_COLUMN_ID), [
    visibleColumns,
  ]);

  const selectorColumn = useMemo(() => allColumns.find(({ id }) => id === SELECT_ALL_COLUMN_ID), [allColumns]);

  useEffect(() => {
    selectorColumn?.toggleHidden(!visibleAdjustmentViewColumns.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleAdjustmentViewColumns]);

  useEffect(() => {
    hiddenColumnsCallback();
  }, [hiddenColumnsCallback]);

  useEffect(() => {
    if (!!resetCount && setIsTableView) {
      setPageSize(INITIAL_PAGE_SIZE);
      dispatch(setIsTableView(!isSmallWidth));
    }
  }, [resetCount, isSmallWidth, setPageSize, setIsTableView, dispatch]);

  // Required to support paginated requests
  useEffect(() => {
    if (fetchRequestCallback) {
      fetchRequestCallback(pageIndex, pageSize, sortBy);
    }
  }, [pageIndex, pageSize, sortBy, fetchRequestCallback]);

  useEffect(() => {
    if (isServerPagination && selectedRows) {
      tableCallbacks?.selectedPaginatedRowsCallback?.(selectedFlatRows, selectedRowIds, selectedRows);
    } else {
      tableCallbacks?.selectedRowsCallback?.(selectedFlatRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlatRowsIds, selectedRowIds, isServerPagination]);

  useEffect(() => {
    tableCallbacks?.visibleColumnsCallback?.(visibleColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns]);

  useEffect(() => {
    if (setIsTableView && isSmallWidth) dispatch(setIsTableView(false));

    if (setIsTableView && !isSmallWidth) dispatch(setIsTableView(true));
  }, [isSmallWidth, dispatch, setIsTableView]);

  const totalItemsTitle = useMemo(() => {
    const totalItems = totalCount ? totalCount : data.length;
    const selectedItemsCount = totalCount ? Object.keys(selectedRowIds).length : selectedFlatRows.length;

    const formattedTotal = thousandFormatter(totalItems);
    const formattedSelected = thousandFormatter(selectedItemsCount);

    if (selectedItemsCount) {
      return t('components.table.selectedItems', {
        selectedItemsCount: formattedSelected,
        totalItems: formattedTotal,
        count: totalItems,
      });
    }

    return t('components.table.totalVehicles', {
      totalItems: formattedTotal,
      count: totalItems,
    });
  }, [data.length, selectedRowIds, t, totalCount, selectedFlatRows.length]);

  const isPaginationVisible = useMemo(() => pageCount > 1 && !!visibleColumns.length, [pageCount, visibleColumns]);

  const toggleTableView = useCallback(
    (value: boolean) => () => {
      if (setIsTableView) dispatch(setIsTableView(value));
    },
    [dispatch, setIsTableView],
  );

  const TableHeaderComponent = (
    <TableHeaderSection>
      {(((isRac || isLender) && user?.is_sso) || !user?.is_sso) && (
        <Button
          title={t('addVehicleModal.button')}
          onClick={() => setShowAddVehicleDialog(true)}
          buttonTheme={isDealerWithInventory ? ButtonVariant.OUTLINE : ButtonVariant.CONTAINED}
          disabled={isAddVehicleModalDisabled[tableName]}
        />
      )}
      {renderAdditionalTableHeaderComponents?.(hiddenColumnsCallback)}
      {isAdjustViewVisible && isTableView && (
        <AdjustDropdown
          allColumns={allColumns}
          toggleHideAllColumns={toggleHideAllColumns}
          defaultColumns={defaultColumns || []}
          customerFriendlyColumns={customerFriendlyColumns || []}
          isDisabled={!isTableView || isTableActionsDisabled}
          closeDropDown={isTableActionsDisabled}
        />
      )}
    </TableHeaderSection>
  );

  return (
    <>
      <TableContainerWrapper>
        <Panel title={title} headerComponent={TableHeaderComponent}>
          {showNoVehicleErrorMessage && (
            <NoVehicleErrorMessage>{t('components.table.noRouteOneVehicleErrorMessage')}</NoVehicleErrorMessage>
          )}
          {showNoVehicleInInventoryErrorMessage && (
            <NoVehicleErrorMessage>
              {t('components.table.noRouteOneVehicleInInventoryErrorMessage')}
            </NoVehicleErrorMessage>
          )}
          {showNoAddedVehicleErrorMessage && (
            <NoVehicleErrorMessage>{t('components.table.noAddedVehicleErrorMessage')}</NoVehicleErrorMessage>
          )}
          {showNoResultsAndDealSourceVehicle && (
            <NoVehicleErrorMessage>
              <Trans
                defaults={t('components.table.noResultsAndRouteOneVehicle')}
                components={[<br />]}
                style={{ maxWidth: '500px' }}
              />
            </NoVehicleErrorMessage>
          )}
          <TableTopSection disabled={isTableFiltersDisabled} data-testid="vehicle-table-filters">
            {children}
            <TableItems $disabled={isTableFiltersDisabled}>
              {!isTableView && selectable && (
                <SelectAllSection>
                  {headerGroups[0]?.headers[0]?.render(TABLE_RENDER_HEADER_KEY)}
                  <SelectAllLabel>{t('components.table.selectAll')}</SelectAllLabel>
                </SelectAllSection>
              )}
              <TotalItems>{totalItemsTitle}</TotalItems>
              {withCardView && isTableView !== undefined && (
                <>
                  {(isRac || isLender) && !isDealerWithInventory ? (
                    <></>
                  ) : (
                    <ToggleCardViewButton
                      selected={!isTableView}
                      onClick={toggleTableView(false)}
                      type="button"
                      data-testid="card-view"
                    />
                  )}
                  <ToggleTableViewButton
                    selected={isTableView}
                    onClick={toggleTableView(true)}
                    type="button"
                    data-testid="table-view"
                  />
                </>
              )}
              <ItemsPerPage setPageSize={setPageSize} currentPageSize={pageSize} gotoPage={gotoPage} />
            </TableItems>
          </TableTopSection>
          {isTableView ? (
            <OverflowTableWrapper>
              <TableWrapper bordered $isRac={isRac} $isLender={isLender} $isFilled={!!rows.length}>
                <TableHeader headerGroups={headerGroups} />
                <TableBody
                  handleOpenDialog={handleOpenDialog}
                  prepareRow={prepareRow}
                  rows={sortedRows}
                  additionalRows={tablePlaceholderType === VehicleTablePlaceholderType.LOADING ? [] : additionalRows}
                  pageSize={pageSize}
                  currentPage={pageIndex}
                  isServerPagination={isServerPagination}
                  isMakeItWork={isMakeItWork}
                  dealSourceVehicleRowIndex={dealSourceVehicleRowIndex}
                  isRac={isRac}
                  isLender={isLender}
                />
              </TableWrapper>
            </OverflowTableWrapper>
          ) : (
            <VehicleCardView
              prepareRow={prepareRow}
              rows={sortedRows}
              pageSize={pageSize}
              currentPage={pageIndex}
              CustomVehicleCard={CustomVehicleCard}
              isServerPagination={isServerPagination}
            />
          )}
          {(isDealerWithInventory || tablePlaceholderType === VehicleTablePlaceholderType.LOADING) && tablePlaceholder}
          {isPaginationVisible && <Pagination gotoPage={gotoPage} pageCount={pageCount} currentPage={pageIndex} />}
        </Panel>
      </TableContainerWrapper>
      {(((isRac || isLender) && user?.is_sso) || !user?.is_sso) && (
        <AddVehicleDialog
          tier={tier}
          show={showAddVehicleDialog}
          onHide={() => setShowAddVehicleDialog(false)}
          lenderName={lenderName}
          currentLenderId={currentLenderId}
          tableName={tableName}
          dealSourceData={dealSourceData}
          isMakeItWork={isMakeItWork}
        />
      )}
    </>
  );
};

export const TableMemo = memo(Table);
